// src/App.jsx
import React, { useEffect, useState } from "react";
import { supabase } from "./lib/supabaseClient";
import {
  urlIngestWebhook,
  seoPackageWebhook,
  makeDraftWebhook,
  INGEST_BATCH_SIZE,
  INGEST_BATCH_DELAY_MS,
  LOG_LIMIT,
} from "./lib/constants";
import {
  sleep,
  normalizeUrl,
  looksLikeUrl,
  canonicalKey,
  parseUrlsWithReport,
  chunk,
  copyText,
  renderValue,
  toCsv,
  daysSince,
} from "./lib/utils";
import { useToast } from "./context/ToastContext.jsx";
import { useAuth } from "./hooks/useAuth";
import { useRequests } from "./hooks/useRequests";
import { useFilters } from "./hooks/useFilters";
import { useRequestActions } from "./hooks/useRequestActions";
import { useContacts } from "./hooks/useContacts";
import { useSEO } from "./hooks/useSEO";
import { useN8n } from "./hooks/useN8n";

import AuthScreen from "./components/AuthScreen";
import { Suspense, lazy } from "react";
const DetailsModal = lazy(() => import("./components/DetailsModal"));
const Header = lazy(() => import("./components/Header"));
const RequestsTab = lazy(() => import("./components/RequestsTab"));
const SeoTab = lazy(() => import("./components/SeoTab"));
const ContactsTab = lazy(() => import("./components/ContactsTab"));
const DatabaseTab = lazy(() => import("./components/DatabaseTab"));
const FoiaDashboard = lazy(() => import("./components/FoiaDashboard"));
const EmailMatcher = lazy(() => import("./features/email-matcher/EmailMatcher"));

export default function App() {
  useEffect(() => {
    document.title = "FOIA App";
  }, []);

  // Toast (from context)
  const { toast, setToast } = useToast();

  // Auth
  const { session, me, authError } = useAuth();

  // Tabs
  const [tab, setTab] = useState("requests");

  // Requests + analytics
  const { requests, setRequests, loadingRequests, loadRequests, analytics } = useRequests(me);
  // Unread Outlook emails count (shared to show in header)
  const [unreadEmailCount, setUnreadEmailCount] = useState(0);

  // Filters (requests + database)
  const filters = useFilters(requests);

  // Selection + details
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [detailRow, setDetailRow] = useState(null);

  // Contacts (owner) - must be before useN8n since it uses contacts
  const {
    contacts,
    loadingContacts,
    contactDraft,
    setContactDraft,
    savingContact,
    editingContact,
    setEditingContact,
    loadContacts,
    saveContact,
    editContact,
    deleteContact,
  } = useContacts(me);

  // Logs + ingest (n8n)
  const { webhookLog, setWebhookLog, urls, setUrls, posting, postUrlsToN8n, onMakeDraft } = useN8n(
    me,
    requests,
    loadRequests,
    contacts
  );

  // SEO
  const { seoUrl, setSeoUrl, seoSubmitting, seoResult, seoAllText, runSeo } = useSEO(
    seoPackageWebhook,
    setWebhookLog,
    LOG_LIMIT
  );

  // Request actions
  const actions = useRequestActions(me, setRequests, setSelectedIds);

  // Load data when org/role ready
  useEffect(() => {
    if (me?.org_id) {
      loadRequests();
      if (me.role === "owner") loadContacts();
    }
  }, [me?.org_id, me?.role]);

  // ---------------- FILTERS (via hook) ----------------
  const {
    // Requests tab
    filterText,
    setFilterText,
    statusFilter,
    setStatusFilter,
    agencyFilter,
    setAgencyFilter,
    locationFilter,
    setLocationFilter,
    sortBy,
    setSortBy,
    agencyOptions,
    locationOptions,
    finalRows,
    totalNotRequested,
    verifiedCount,
    unverifiedCount,
    shownCount,
    anyFilterActive,
    isVerifiedRow,
    // Database tab
    dbFilterText,
    setDbFilterText,
    dbStatus,
    setDbStatus,
    dbStatusOptions,
    dbAgency,
    setDbAgency,
    dbAgencyOptions,
    dbLocation,
    setDbLocation,
    dbLocationOptions,
    dbDateFrom,
    setDbDateFrom,
    dbDateTo,
    setDbDateTo,
    dbOverdueOnly,
    setDbOverdueOnly,
    dbRows,
    // Saved search functions
    getCurrentFilters,
    loadFilters,
  } = filters;

  // ---------------- ACTIONS: n8n kept ----------------
  // Ingest + draft moved to useN8n

  // Map actions from hook
  const onMarkTrash = actions.onMarkTrash;
  const onMarkDenied = actions.onMarkDenied;
  const onMarkReceived = actions.onMarkReceived;
  const onMarkDelayed = actions.onMarkDelayed;
  const onMarkNoBodycam = actions.onMarkNoBodycam;
  const onMarkRequested = actions.onMarkRequested;
  const onInvoice = actions.onInvoice;
  const onEditRequest = actions.onEditRequest;
  const onDeleteRequest = actions.onDeleteRequest;

  // Bulk wrappers using selectedIds state
  const onBulkMarkRequested = () => actions.onBulkMarkRequested(selectedIds);
  const onBulkTrash = () => actions.onBulkTrash(selectedIds);

  // Contacts functions already provided by useContacts hook

  // SEO handled by useSEO hook

  // ---------------- AUTH UI ----------------
  if (!session) {
    return <AuthScreen />;
  }
  if (!me) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 p-6 shadow-lg shadow-slate-200/50 dark:shadow-black/20">
          <div className="text-lg font-semibold mb-2 text-slate-900 dark:text-gray-100">Loading…</div>
          {authError && (
            <div className="mt-2 text-red-600 dark:text-red-400 text-sm">{authError}</div>
          )}
        </div>
      </div>
    );
  }

  // ---------------- MAIN UI ----------------
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading UI…</div>}>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-gray-100 font-sans antialiased">
        <div className="relative">
          {/* Header */}
          <div className="sticky top-0 z-50 bg-slate-50/95 dark:bg-slate-950/95 backdrop-blur-sm border-b border-slate-200 dark:border-white/10">
            <div className="mx-auto max-w-7xl px-8 py-6">
              <Header 
                tab={tab} 
                setTab={setTab} 
                me={me} 
                onSignOut={() => supabase.auth.signOut()} 
                requests={[...requests, ...dbRows]}
                unreadCount={unreadEmailCount}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="mx-auto max-w-7xl px-8 py-8">

            {/* ===================== REQUESTS ===================== */}
            {tab === "requests" && (
              <RequestsTab
                urls={urls}
                setUrls={setUrls}
                posting={posting}
                postUrlsToN8n={postUrlsToN8n}
                INGEST_BATCH_SIZE={INGEST_BATCH_SIZE}
                INGEST_BATCH_DELAY_MS={INGEST_BATCH_DELAY_MS}
                analytics={analytics}
                loadRequests={loadRequests}
                loadingRequests={loadingRequests}
                webhookLog={webhookLog}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                onBulkMarkRequested={onBulkMarkRequested}
                onBulkTrash={onBulkTrash}
                totalNotRequested={totalNotRequested}
                verifiedCount={verifiedCount}
                unverifiedCount={unverifiedCount}
                filterText={filterText}
                setFilterText={setFilterText}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                agencyFilter={agencyFilter}
                setAgencyFilter={setAgencyFilter}
                locationFilter={locationFilter}
                setLocationFilter={setLocationFilter}
                sortBy={sortBy}
                setSortBy={setSortBy}
                agencyOptions={agencyOptions}
                locationOptions={locationOptions}
                shownCount={shownCount}
                anyFilterActive={anyFilterActive}
                finalRows={finalRows}
                isVerifiedRow={isVerifiedRow}
                renderValue={renderValue}
                onMakeDraft={onMakeDraft}
                onMarkRequested={onMarkRequested}
                onMarkTrash={onMarkTrash}
                onMarkDelayed={onMarkDelayed}
                onMarkDenied={onMarkDenied}
                onMarkReceived={onMarkReceived}
                onMarkNoBodycam={onMarkNoBodycam}
                onInvoice={onInvoice}
                onEditRequest={onEditRequest}
                onDeleteRequest={onDeleteRequest}
                detailRow={detailRow}
                setDetailRow={setDetailRow}
                getCurrentFilters={getCurrentFilters}
                loadFilters={loadFilters}
              />
            )}

            {/* ===================== DASHBOARD ===================== */}
            {tab === "dashboard" && me?.org_id && (
              <FoiaDashboard orgId={me.org_id} />
            )}

            {/* ===================== DATABASE ===================== */}
            {/* Dashboard removed */}

            {/* ===================== DATABASE (ALL REQUESTS) ===================== */}
            {tab === "database" && (
              <DatabaseTab
                dbFilterText={dbFilterText}
                setDbFilterText={setDbFilterText}
                dbStatus={dbStatus}
                setDbStatus={setDbStatus}
                dbStatusOptions={dbStatusOptions}
                dbAgency={dbAgency}
                setDbAgency={setDbAgency}
                dbAgencyOptions={dbAgencyOptions}
                dbLocation={dbLocation}
                setDbLocation={setDbLocation}
                dbLocationOptions={dbLocationOptions}
                dbDateFrom={dbDateFrom}
                setDbDateFrom={setDbDateFrom}
                dbDateTo={dbDateTo}
                setDbDateTo={setDbDateTo}
                dbOverdueOnly={dbOverdueOnly}
                setDbOverdueOnly={setDbOverdueOnly}
                loadingRequests={loadingRequests}
                dbRows={dbRows}
                loadRequests={loadRequests}
                toCsv={toCsv}
                daysSince={daysSince}
                detailRow={detailRow}
                setDetailRow={setDetailRow}
                onMakeDraft={onMakeDraft}
                onMarkRequested={onMarkRequested}
                onMarkReceived={onMarkReceived}
                onMarkDenied={onMarkDenied}
                onMarkDelayed={onMarkDelayed}
                onMarkNoBodycam={onMarkNoBodycam}
                onInvoice={onInvoice}
                onMarkTrash={onMarkTrash}
                onEditRequest={onEditRequest}
                onDeleteRequest={onDeleteRequest}
                getCurrentFilters={getCurrentFilters}
                loadFilters={loadFilters}
              />
            )}

            {/* ===================== EMAIL MATCHER ===================== */}
            {tab === "email-matcher" && me?.org_id && (
              <EmailMatcher 
                orgId={me.org_id} 
                me={me}
                setRequests={setRequests}
                setUnreadCount={setUnreadEmailCount}
              />
            )}

            {/* ===================== SEO ===================== */}
            {tab === "seo" && (
              <SeoTab
                seoUrl={seoUrl}
                setSeoUrl={setSeoUrl}
                seoSubmitting={seoSubmitting}
                runSeo={runSeo}
                seoPackageWebhook={seoPackageWebhook}
                seoResult={seoResult}
                seoAllText={seoAllText}
                copyText={copyText}
                setToast={setToast}
              />
            )}

            {/* ===================== CONTACTS (OWNER) ===================== */}
            {tab === "contacts" && me.role === "owner" && (
              <ContactsTab
                editingContact={editingContact}
                contactDraft={contactDraft}
                setEditingContact={setEditingContact}
                setContactDraft={setContactDraft}
                saveContact={saveContact}
                deleteContact={deleteContact}
                savingContact={savingContact}
                contacts={contacts}
                loadingContacts={loadingContacts}
                loadContacts={loadContacts}
                setToast={setToast}
              />
            )}

            {/* Toast Notification */}
            {toast && (
              <div
                className={`
                  fixed bottom-6 right-6 rounded-xl px-6 py-4 text-sm font-medium 
                  backdrop-blur-sm border animate-slide-up z-50
                  transition-all duration-300 shadow-xl
                  ${
                    toast.type === "ok"
                      ? "bg-green-500/20 text-green-300 border-green-500/30"
                      : toast.type === "warn"
                      ? "bg-amber-500/20 text-amber-300 border-amber-500/30"
                      : toast.type === "error"
                      ? "bg-red-500/20 text-red-300 border-red-500/30"
                      : "bg-slate-900 text-gray-100 border-white/10"
                  }
                `}
                onAnimationEnd={() => setTimeout(() => setToast(null), 3000)}
              >
                <div className="flex items-center gap-3">
                  {toast.type === "ok" && (
                    <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {toast.type === "warn" && (
                    <svg className="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                  {toast.type === "error" && (
                    <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <span>{toast.msg}</span>
                </div>
              </div>
            )}

            <footer className="mt-24 text-center text-xs text-gray-500">
              Built for and by Jordy Kooij.
            </footer>
          </div>
        </div>
      </div>
    </Suspense>
  );
}

